'use no memo';

import { useSuspenseQuery } from '@tanstack/react-query';
import { Outlet, createFileRoute, useLocation } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Suspense } from 'react';
import { useShallow } from 'zustand/shallow';
import { VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { shouldHideSidebarPortal, SidebarPortalNav, SidebarPortalShell, useSidebarPortalStore } from '@/components/SidebarPortal';
import { Separator } from '@/components/ui/separator';
import { DeviceCatalogQueryProvider } from '@/features/form-factory';
import { DeviceQueryProvider } from '@/features/form-factory/context/DeviceQueryContext';
import { useDesktopOrMobileMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/lib/auth';
import { defaultNS, useTranslation } from '@/lib/i18n';
import GlobalHeaderPc from './_individual/individual/-components/GlobalHeader/GlobalHeaderPc';
import GlobalHeaderSp from './_individual/individual/-components/GlobalHeader/GlobalHeaderSp';
import { fetchIndividualDeviceCatalogById, useIndividualDeviceCatalogsForSelect } from './_individual/individual/device-catalog/-query/useDeviceCatalogsForSelect';
import { fetchIndividualDeviceById, useIndividualDevicesForSelect } from './_individual/individual/device/-query/useDevicesForSelect';
export const Route = createFileRoute('/_individual')({
  component: IndividualLayout
});

/**
 * individual 配下のマウント時に `/v2/session` を再取得してストアを更新する。
 * staff 側の `useProductAccessCheck`（Sidebar）と同じく、Role / dataScopes を
 * 都度最新化するための仕掛け。永続化された古いセッションのままリロードで
 * 更新されない問題を解消する。フルリロード時のみ再取得され（React Query の
 * インメモリキャッシュは空）、individual 内の SPA 遷移では再取得しない。
 */
function IndividualSessionGate({
  children
}: {
  children: ReactNode;
}) {
  const refreshUserSession = useAuthStore(useShallow(({
    refreshUserSession
  }) => refreshUserSession));
  useSuspenseQuery({
    queryKey: ['individualSessionRefresh'],
    queryFn: refreshUserSession
  });
  return <>{children}</>;
}
function IndividualLayout() {
  const {
    isDesktop
  } = useDesktopOrMobileMediaQuery();
  const {
    pathname
  } = useLocation();
  const sideNavOpen = useSidebarPortalStore(state => state.isOpen);
  const toggleSideNav = useSidebarPortalStore(state => state.toggleOpen);
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home.nav'
  });

  // 実行フロー（タスク実行・契約署名・手続きパッケージ実行）と、自前 sidebar を持つ
  // portal モック画面では常設サイドナビを出さない。判定は SidebarPortal feature に集約し、
  // モバイルメニュー（SidebarPortalMobileMenu）と共有する。
  const hideSideNav = shouldHideSidebarPortal(pathname);
  return <DeviceCatalogQueryProvider useDeviceCatalogsForSelect={useIndividualDeviceCatalogsForSelect} fetchDeviceCatalogById={fetchIndividualDeviceCatalogById}>
      <DeviceQueryProvider useDevicesForSelect={useIndividualDevicesForSelect} fetchDeviceById={fetchIndividualDeviceById}>
        {/* TODO: fixed inset-0 はビューポートに貼り付けることで html/body スクロールを回避している。
            根本対応として html, body, #root に overflow-hidden を設定し、h-dvh に置き換えることを検討する。 */}
        <VStack className="fixed inset-0 overflow-hidden bg-blue-50">
          {isDesktop ? <GlobalHeaderPc /> : <GlobalHeaderSp />}
          <Separator />
          <div className="flex min-h-0 flex-1">
            {isDesktop && !hideSideNav ? <SidebarPortalShell open={sideNavOpen} onToggle={toggleSideNav} ariaLabel={t('menu')} alwaysRenderChildren>
                <SidebarPortalNav showLabel={sideNavOpen} />
              </SidebarPortalShell> : null}
            {/* /individual 配下の共通背景: neutral-bottom-fill の下地に blue-50/30 を重ねる
                （mock portal と統一）。個別ページ側で背景を敷かない。 */}
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-neutral-bottom-fill">
              <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-blue-50/30">
                <Suspense fallback={<ViewportLoader />}>
                  <IndividualSessionGate>
                    <Outlet />
                  </IndividualSessionGate>
                </Suspense>
              </div>
            </main>
          </div>
        </VStack>
      </DeviceQueryProvider>
    </DeviceCatalogQueryProvider>;
}